// Bridge between the native outfitter mobile app and the dashboard when the
// dashboard runs embedded inside a react-native-webview. The native app owns
// authentication and pushes access tokens into the page; no Auth0 login ever
// happens inside the webview.
//
// Protocol (must match the mobile implementation exactly):
// - The webview is opened with `?in_app=1` on the initial URL (any non-`false`
//   value works; `?in_app=false` is an explicit reset). The same flag hides
//   the dashboard chrome and switches auth to this bridge.
// - The native app evaluates JS in the main frame that sets
//   `window.__MB_NATIVE_AUTH__ = { accessToken }` and dispatches a
//   `mb-native-auth` event. Pushes can happen at any time and repeat
//   (token refreshes), before or after the app boots.
//
// Native-side contract:
// - Inject the token *before* content loads (e.g. react-native-webview's
//   `injectedJavaScriptBeforeContentLoaded`) and re-push on an interval.
//   Requests that time out waiting for the first push are refetched
//   automatically when a token finally arrives, but first paint is fastest
//   when the token is present before the app boots.
// - The web side has no way to request a refresh: push a fresh token
//   proactively before the current one expires, or API requests will start
//   failing with auth errors.
import COPY_CONSTANTS from "~config/copy-constants"
import {
    IS_IN_APP_BROWSER_QUERY_PARAM,
    IS_IN_APP_BROWSER_SESSION_STORAGE_KEY,
} from "~config/constants"
import { getUrlQueryParam } from "~utils/helpers/helpers"

export const NATIVE_AUTH_GLOBAL_KEY = "__MB_NATIVE_AUTH__"
export const NATIVE_AUTH_EVENT_NAME = "mb-native-auth"
export const NATIVE_AUTH_TOKEN_TIMEOUT_MS = 10_000

const TRUE_VALUE = "true"
const FALSE_VALUE = "false"

let didBridgeTimeoutOccur = false

/**
 * True when the `in_app` query param was present on a load in this browsing
 * session. Any non-`false` value enables in-app mode and `?in_app=false` is
 * an explicit reset. The flag can never turn on without the query param;
 * once seen it is persisted to sessionStorage so in-webview navigation keeps
 * in-app mode without re-passing the param.
 * SSR-safe: always false during Gatsby server rendering.
 */
export function isInAppMode(): boolean {
    if (isServerSide()) return false

    const param = getUrlQueryParam(IS_IN_APP_BROWSER_QUERY_PARAM)

    // Explicit reset so a tab can leave in-app mode without being closed.
    if (param === FALSE_VALUE) {
        window.sessionStorage.removeItem(IS_IN_APP_BROWSER_SESSION_STORAGE_KEY)
        return false
    }

    if (param) {
        window.sessionStorage.setItem(
            IS_IN_APP_BROWSER_SESSION_STORAGE_KEY,
            TRUE_VALUE
        )
        return true
    }

    return (
        window.sessionStorage.getItem(IS_IN_APP_BROWSER_SESSION_STORAGE_KEY) ===
        TRUE_VALUE
    )
}

/**
 * Resolves with the access token pushed by the native app. Resolves
 * immediately when a token has already been pushed, otherwise waits for the
 * next push event. The token lives in memory only — it must never be
 * persisted to storage, cookies, or logs.
 */
export function getAccessTokenFromBridge(): Promise<string> {
    if (isServerSide()) {
        return Promise.reject(createBridgeTimeoutError())
    }

    // Always read the global at resolve time — a refresh push may have
    // replaced the payload since the event was dispatched.
    const accessToken = readBridgeAccessToken()
    if (accessToken) return Promise.resolve(accessToken)

    return new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => {
            window.removeEventListener(NATIVE_AUTH_EVENT_NAME, handleAuthEvent)
            didBridgeTimeoutOccur = true
            reject(createBridgeTimeoutError())
        }, NATIVE_AUTH_TOKEN_TIMEOUT_MS)

        function handleAuthEvent() {
            const pushedToken = readBridgeAccessToken()
            if (!pushedToken) return

            clearTimeout(timeoutId)
            window.removeEventListener(NATIVE_AUTH_EVENT_NAME, handleAuthEvent)
            resolve(pushedToken)
        }

        window.addEventListener(NATIVE_AUTH_EVENT_NAME, handleAuthEvent)
    })
}

/**
 * Invokes `onToken` with the current token (when already pushed) and with
 * every subsequent push from the native app. Returns an unsubscribe function.
 */
export function subscribeToNativeAuthToken(
    onToken: (accessToken: string) => void
): () => void {
    if (isServerSide()) return noop

    const handleAuthEvent = () => {
        const accessToken = readBridgeAccessToken()
        if (accessToken) onToken(accessToken)
    }

    // A push may have landed before this subscriber mounted.
    handleAuthEvent()
    window.addEventListener(NATIVE_AUTH_EVENT_NAME, handleAuthEvent)

    return () =>
        window.removeEventListener(NATIVE_AUTH_EVENT_NAME, handleAuthEvent)
}

/**
 * True (once) when a token request timed out since the last call. Lets the
 * Apollo layer refetch the queries that failed waiting for the first push
 * when a token finally arrives.
 */
export function consumeBridgeTimeoutFlag(): boolean {
    const didOccur = didBridgeTimeoutOccur
    didBridgeTimeoutOccur = false
    return didOccur
}

function readBridgeAccessToken(): string | null {
    const payload: unknown = Reflect.get(window, NATIVE_AUTH_GLOBAL_KEY)
    if (!isNativeAuthPayload(payload)) return null

    return payload.accessToken || null
}

function isNativeAuthPayload(value: unknown): value is NativeAuthPayload {
    if (typeof value !== "object" || value === null) return false
    if (!("accessToken" in value)) return false

    return typeof value.accessToken === "string"
}

function createBridgeTimeoutError(): Error {
    return new Error(COPY_CONSTANTS.NATIVE_AUTH_TOKEN_TIMEOUT_ERROR)
}

function isServerSide(): boolean {
    return typeof window === "undefined"
}

function noop(): void {
    // Intentionally empty — returned as the unsubscribe function on the server.
}

interface NativeAuthPayload {
    accessToken: string
}
